import { BrandAssetContent, LogoType } from "./types";

export const GLOBAL_CORPORATE_ID = 1;
export const DEFAULT_LOGO_HEIGHT = 32;
export const PARAM_LOGO_HEIGHT_KEY = "logoHeight";
export const ZIP_SUFFIX = "BRANDASSET";
export const COLORS_SUFFIX = "COLORS_OL";
export const PARAMETERS_SUFFIX = "PARAMETERS";

export const LOGO_DESCRIPTORS: {
  type: LogoType;
  suffix: string;
  ext: "png" | "svg";
  mime: string;
  accept: string;
}[] = [
  {
    type: "rgb",
    suffix: "LOGO_RGB",
    ext: "png",
    mime: "image/png",
    accept: "image/png",
  },
  {
    type: "white",
    suffix: "LOGO_WHITE",
    ext: "png",
    mime: "image/png",
    accept: "image/png",
  },
  {
    type: "black",
    suffix: "LOGO_BLACK",
    ext: "png",
    mime: "image/png",
    accept: "image/png",
  },
  {
    type: "favicon",
    suffix: "FAVICON",
    ext: "png",
    mime: "image/png",
    accept: "image/png",
  },
  {
    type: "mobile",
    suffix: "LOGO_MOBILE",
    ext: "svg",
    mime: "image/svg+xml",
    accept: "image/svg+xml,.svg",
  },
];

export const LOGO_TYPES: LogoType[] = LOGO_DESCRIPTORS.map((d) => d.type);

export const REQUIRED_COLOR_VARIABLES: string[] = [
  "backgroundAccent",
  "backgroundDisabled",
  "backgroundDisabledAlternative",
  "backgroundPrimary",
  "backgroundPrimaryCta",
  "backgroundPrimaryLighter",
  "backgroundPrimaryLighterAlternative",
  "backgroundSaved",
  "backgroundSecondary",
  "backgroundSecondaryComplete",
  "backgroundWarning",
  "backgroundChatBotContainer",
  "inputBackground",
  "border",
  "borderAccent",
  "borderError",
  "borderPrimaryCta",
  "borderSecondaryComplete",
  "borderTag",
  "borderGrayTag",
  "borderWarning",
  "textDisabled",
  "textDisabledAlternative",
  "textError",
  "textPrimary",
  "textMandatory",
  "textPrimaryAlternative",
  "textPrimaryCta",
  "textInput",
  "textTag",
  "textWarning",
  "textFooter",
  "headerIcon",
  "textWidget",
  "textWidgetAlternative",
  "exploreCardText",
  "systemDisabled",
  "systemPrimary01",
  "systemPrimary01Alternative01",
  "systemPrimary01Alternative02",
  "systemPrimary02",
  "systemSecondary01",
  "systemSecondary02",
  "systemSecondary03",
  "systemSecondary04",
  "systemSecondary05",
  "accentVariantA",
  "accentVariantB",
  "accentVariantC",
  "accentVariantD",
  "accentVariantE",
  "drawerWidgetBg",
  "white",
  "gradient01Start",
  "gradient01End",
  "gradient02Start",
  "gradient02End",
  "gradient03Start",
  "gradient03End",
  "gradient04Start",
  "gradient04End",
  "gradient05Start",
  "gradient05End",
  "gradient06Start",
  "gradient06End",
  "gradient07Start",
  "gradient07End",
  "gradient08Start",
  "gradient08End",
  "backgroundFooter",
  "backgroundSecondaryAlternative",
  "backgroundSecondaryHover",
  "backgroundPrimaryCtaHover",
  "backgroundCard",
  "backgroundSecondaryCtaHover",
  "borderPrimaryCtaHover",
  "borderPrimaryControl",
  "borderPrimaryControlHover",
  "backgroundPrimaryControl",
  "backgroundPrimaryControlHover",
  "backgroundPrimaryControlDisabled",
  "borderPrimaryControlDisabled",
  "textPrimaryControl",
  "textPrimaryControlDisabled",
  "systemSecondary01Hover",
  "backgroundError",
];

export interface ColorVariablesValidation {
  valid: boolean;
  missing: string[];
}

export const validateColorVariables = (
  colors: Record<string, string> | undefined | null,
): ColorVariablesValidation => {
  const present = new Set(colors ? Object.keys(colors) : []);
  const missing = REQUIRED_COLOR_VARIABLES.filter((name) => !present.has(name));
  return { valid: missing.length === 0, missing };
};

export const EMPTY_BRAND_ASSET_CONTENT: BrandAssetContent = {
  colors: {},
  parameters: { [PARAM_LOGO_HEIGHT_KEY]: DEFAULT_LOGO_HEIGHT },
  logos: { rgb: "", white: "", black: "", favicon: "", mobile: "" },
};

export const buildZipName = (corporateName: string): string =>
  `${corporateName}_${ZIP_SUFFIX}.zip`;

export const buildAssetName = (
  corporateName: string,
  suffix: string,
  ext: string,
): string => `${corporateName}_${suffix}.${ext}`;

export const isHexColor = (value: string): boolean =>
  /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(
    (value || "").trim(),
  );

export const isGradient = (value: string): boolean =>
  /gradient\s*\(/i.test((value || "").trim());

export const isRgbaTuple = (value: string): boolean =>
  /^(\d{1,3}\s*,\s*){2}\d{1,3}(\s*,\s*(0|1|0?\.\d+))?$/.test(
    (value || "").trim(),
  );

export const normalizeHexTo6 = (hex: string): string => {
  if (hex.length === 4)
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  if (hex.length === 9) return hex.slice(0, 7);
  return hex;
};

export type GradientStop = { index: number; raw: string; hex6: string };

const rgbComponentsToHex6 = (r: number, g: number, b: number): string =>
  `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;

export const extractGradientStops = (gradient: string): GradientStop[] => {
  const stops: GradientStop[] = [];
  let m: RegExpExecArray | null;

  const hexRe = /#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})\b/g;
  while ((m = hexRe.exec(gradient)) !== null)
    stops.push({ index: m.index, raw: m[0], hex6: normalizeHexTo6(m[0]) });

  const rgbaRe =
    /rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*[\d.]+)?\s*\)/g;
  while ((m = rgbaRe.exec(gradient)) !== null)
    stops.push({
      index: m.index,
      raw: m[0],
      hex6: rgbComponentsToHex6(parseInt(m[1]), parseInt(m[2]), parseInt(m[3])),
    });

  return stops.sort((a, b) => a.index - b.index);
};

export const replaceGradientStop = (
  gradient: string,
  index: number,
  newHex: string,
): string => {
  const stops = extractGradientStops(gradient);
  if (index >= stops.length) return gradient;
  const { index: pos, raw } = stops[index];
  return gradient.slice(0, pos) + newHex + gradient.slice(pos + raw.length);
};

export const toCssColor = (value: string): string => {
  const v = (value || "").trim();
  if (!v) return "transparent";
  if (isHexColor(v)) return v;
  if (/^(\d{1,3}\s*,\s*){2}\d{1,3}(\s*,\s*(0|1|0?\.\d+))?$/.test(v)) {
    return `rgba(${v}${v.split(",").length === 3 ? ",1" : ""})`;
  }
  return v;
};

export const isValidLogoFile = (file: File, type: LogoType): boolean => {
  const descriptor = LOGO_DESCRIPTORS.find((d) => d.type === type);
  if (!descriptor) return false;
  const name = file.name.toLowerCase();
  if (descriptor.ext === "svg") {
    return file.type === "image/svg+xml" || name.endsWith(".svg");
  }
  return file.type === "image/png" || name.endsWith(".png");
};
